import { ComponentProps, CSSProperties } from 'react';
import { Toaster as Sonner, type ToastClassnames } from 'sonner';
import { useThemeStore } from '@/Domain/store/theme.store';

type ToasterProps = ComponentProps<typeof Sonner>;

/** For a message too long to share a row with its action: the action moves under the text. `!` beats Sonner's unlayered CSS. */
export const STACKED_ACTION_TOAST_CLASS_NAMES: ToastClassnames = {
  toast: '!grid grid-cols-[auto_1fr] !items-start !gap-x-1.5 !gap-y-3',
  actionButton:
    'col-start-2 justify-self-start !ml-0 !h-[26px] !rounded-lg !px-2.5 !text-xs font-medium hover:opacity-85',
};

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme } = useThemeStore();

  return (
    <Sonner
      className="toaster group"
      theme={theme as ToasterProps['theme']}
      richColors
      expand
      visibleToasts={3}
      gap={12}
      style={
        {
          '--border-radius': '0.75rem',
          '--normal-bg': 'var(--card)',
          '--normal-border': 'var(--border)',
          '--normal-text': 'var(--card-foreground)',
          '--success-bg': 'var(--success-muted)',
          '--success-border': 'var(--success-muted)',
          '--success-text': 'var(--foreground)',
          '--error-bg': 'var(--destructive-muted)',
          '--error-border': 'var(--destructive-muted)',
          '--error-text': 'var(--foreground)',
          '--warning-bg': 'var(--warning-muted)',
          '--warning-border': 'var(--warning-muted)',
          '--warning-text': 'var(--foreground)',
          '--info-bg': 'var(--info-muted)',
          '--info-border': 'var(--info-muted)',
          '--info-text': 'var(--foreground)',
        } as CSSProperties
      }
      toastOptions={{
        classNames: {
          toast: 'shadow-lg',
          content: 'gap-3',
          title: 'text-sm font-medium',
          description: 'text-sm',
          icon: 'mt-0.5 self-start',
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
